import { useState, type FormEvent } from "react";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { AuthLayout } from "../components/AuthLayout";
import { Button } from "../components/Button";
import { FieldInput } from "../components/Field";
import { LoadingScreen } from "../components/LoadingScreen";
import { MessageBanner } from "../components/MessageBanner";
import { useSession } from "../lib/session/SessionContext";

const MIN_PASSWORD_LENGTH = 6;

export function ResetPasswordScreen() {
  const { status, updatePassword, error, logout } = useSession();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (status === "loading") return <LoadingScreen label="Validando seu link..." />;

  if (status === "anon") {
    return (
      <AuthLayout
        title="Link inválido"
        subtitle="Este link de recuperação expirou ou já foi utilizado. Solicite um novo link na tela de login."
        footer={<span>Apogeu App · alunos e professores</span>}
      >
        <Button fullWidth onClick={() => window.location.assign("/")}>Voltar para o login</Button>
      </AuthLayout>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setLocalError(`A senha precisa de ao menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }
    if (password !== confirm) {
      setLocalError("As senhas não coincidem.");
      return;
    }
    setBusy(true);
    try {
      await updatePassword(password);
      setDone(true);
    } catch {
      // erro já normalizado pelo contexto
    } finally {
      setBusy(false);
    }
  };

  const message = localError ?? error;

  if (done) {
    return (
      <AuthLayout
        title="Senha atualizada"
        subtitle="Seu acesso está pronto. Use a nova senha para entrar no Apogeu App."
        footer={<span>Apogeu App · alunos e professores</span>}
      >
        <Button fullWidth onClick={() => window.location.assign("/")}>Continuar</Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Crie uma nova senha"
      subtitle="Escolha uma senha nova para recuperar o acesso à sua conta."
      footer={<span>Apogeu App · alunos e professores</span>}
    >
      <form className="auth-form" onSubmit={submit}>
        <FieldInput
          label="Nova senha"
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          placeholder="Nova senha"
          value={password}
          onChange={e => setPassword(e.target.value)}
          trailing={
            <button
              type="button"
              className="field__action"
              onClick={() => setShowPassword(value => !value)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />
        <FieldInput
          label="Confirme a nova senha"
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          placeholder="Repita a nova senha"
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
        />
        {message && <MessageBanner>{message}</MessageBanner>}
        <Button type="submit" fullWidth disabled={busy}>
          <KeyRound size={17} aria-hidden="true" />
          {busy ? "Atualizando..." : "Salvar nova senha"}
        </Button>
        <button type="button" className="link-button link-button--center" onClick={() => void logout()}>
          Sair desta conta
        </button>
      </form>
    </AuthLayout>
  );
}
